import React, { useEffect, useRef, useState } from "react";
import { authFetch } from "../../../../utils/cmsAuth";
import { formatEstimatedKrw, USD_KRW_REFERENCE } from "./captionLabCurrency";
import "./CaptionLab.css";

const BASE = "/concierge/caption-lab";
const LIMIT = 10;
const STATUS = { SUCCESS: "완료", RUNNING: "진행 중", PARTIAL: "일부 실패", FAILED: "실패", EMPTY: "생성 결과 없음", TRUNCATED: "출력 한도 도달" };
const ERRORS = {
  INSUFFICIENT_CREDITS: "크레딧 잔액 부족",
  KEY_MISSING: "서버 API 키 미설정", TIMEOUT: "API 응답 시간 초과", NETWORK_ERROR: "API 연결 실패",
  HTTP_400: "API 요청 설정 오류",
  HTTP_401: "API 키 인증 실패", HTTP_403: "모델 사용 권한 없음", HTTP_404: "모델 또는 API 경로 사용 불가",
  HTTP_429: "API 사용 한도 초과", INVALID_RESPONSE: "API 응답 형식 오류", INCOMPLETE_RESPONSE: "생성이 완료되지 않음",
};

async function api(path, options = {}) {
  const response = await authFetch(`${BASE}${path}`, {
    ...options, headers: { "Content-Type": "application/json", ...options.headers },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(typeof body.detail === "string" ? body.detail : "요청을 처리하지 못했습니다.");
    error.status = response.status;
    throw error;
  }
  return body;
}

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) throw new Error("안전한 요청 ID를 만들 수 없습니다. HTTPS 주소와 최신 브라우저를 사용해 주세요.");
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function dateLabel(value) {
  return value ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : "-";
}

function ResultCard({ result, mode }) {
  if (!result) return <div className="ccl-result ccl-empty">매장과 모델 2개를 선택해 비교를 시작하세요.</div>;
  const text = mode === "raw" ? result.raw_caption : result.final_caption;
  return <article className="ccl-result">
    <header><h3>{result.label}</h3><span className={result.status === "SUCCESS" ? "ccl-badge" : "ccl-badge ccl-warning"}>{STATUS[result.status] || result.status}</span></header>
    <div className="ccl-metrics">
      <span>입력 <b>{result.input_tokens ?? "-"}</b></span>
      <span>출력(추론 포함) <b>{result.output_tokens ?? "-"}</b></span>
      <span>추론(출력의 일부) <b>{result.reasoning_tokens ?? "-"}</b></span>
      <span>총 토큰 <b>{result.llm_total_tokens ?? "-"}</b></span>
      <span>소요 <b>{result.elapsed_seconds ?? "-"}초</b></span>
    </div>
    <div className="ccl-call-cost"><span>이 모델 1회 총 예상 비용</span><strong>{result.llm_estimated_cost_usd != null ? `$${result.llm_estimated_cost_usd.toFixed(6)}` : "계산 불가"}</strong>
      {formatEstimatedKrw(result.llm_estimated_cost_usd) && <span className="ccl-krw-cost">{formatEstimatedKrw(result.llm_estimated_cost_usd)}</span>}
      <small>입력 + 출력(추론 포함) · 일반 단가 기준, 캐시 할인·크레딧·세금 제외</small></div>
    {result.error_code && <p className="ccl-warning">{ERRORS[result.error_code] || `API 오류 (${result.error_code})`}</p>}
    {result.error_detail && <p className="ccl-warning">{result.error_detail}</p>}
    {result.used_fallback && <p className="ccl-warning">AI 결과 없음. 최종 게시 형태에서는 대체 문구 사용.</p>}
    <pre>{text || "AI 생성 문구가 없습니다."}</pre>
    <details><summary>생성 설정·가격 기준</summary><pre>{JSON.stringify({ settings: result.settings, pricing: result.pricing?.rates }, null, 2)}</pre></details>
  </article>;
}

export default function CaptionLab() {
  const [models, setModels] = useState([]);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [promptPreview, setPromptPreview] = useState(null);
  const [userPrompt, setUserPrompt] = useState("");
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState("");
  const [promptRevision, setPromptRevision] = useState(0);
  const [modelIds, setModelIds] = useState(["gpt-4o", "gpt-6.1-sol"]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);
  const [mode, setMode] = useState("raw");
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(0);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyRevision, setHistoryRevision] = useState(0);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const pendingRequest = useRef(null);
  const running = useRef(false);
  const alive = useRef(true);
  const detailVersion = useRef(0);

  useEffect(() => {
    alive.current = true;
    api("/models").then((data) => { if (alive.current) setModels(data.items); })
      .catch((cause) => { if (alive.current) setError(cause.message); });
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setHistoryBusy(true);
    api(`/tests?limit=${LIMIT}&offset=${page * LIMIT}`).then((data) => { if (active) setHistory(data); })
      .catch((cause) => { if (active) setError(cause.message); })
      .finally(() => { if (active) setHistoryBusy(false); });
    return () => { active = false; };
  }, [page, historyRevision]);

  useEffect(() => {
    let active = true;
    setMatches([]);
    if (!query.replace(/[\s_]/g, "") || selected) { setSearching(false); return undefined; }
    setSearching(true);
    const timer = window.setTimeout(() => {
      api(`/stores?q=${encodeURIComponent(query)}`).then((data) => { if (active) setMatches(data.items); })
        .catch((cause) => { if (active) setError(cause.message); })
        .finally(() => { if (active) setSearching(false); });
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [query, selected]);

  useEffect(() => {
    let active = true;
    setPromptPreview(null);
    setUserPrompt("");
    setPromptError("");
    setPromptLoading(Boolean(selected));
    if (!selected) return undefined;
    api(`/stores/${selected.user_id}/prompt`).then((data) => {
      if (!active) return;
      setPromptPreview(data);
      setUserPrompt(data.user_prompt);
    }).catch((cause) => { if (active) setPromptError(cause.message); })
      .finally(() => { if (active) setPromptLoading(false); });
    return () => { active = false; };
  }, [selected, promptRevision]);

  const chosen = modelIds.map((id) => models.find((item) => item.id === id));
  const promptReady = selected && promptPreview?.user_id === selected.user_id && !promptLoading;
  const canRun = promptReady && userPrompt.trim() && modelIds[0] !== modelIds[1] && chosen.every((item) => item?.configured) && !busy && !loadingRecord;

  async function run() {
    if (!canRun || running.current) return;
    running.current = true;
    setBusy(true);
    setError("");
    const fingerprint = JSON.stringify([selected.user_id, modelIds, userPrompt]);
    if (pendingRequest.current && pendingRequest.current.fingerprint !== fingerprint) {
      setError("응답 확인이 끝나지 않은 테스트가 있습니다. 기존 매장·모델·프롬프트 조합으로 재시도하거나 히스토리를 먼저 확인해 주세요.");
      setBusy(false);
      running.current = false;
      return;
    }
    try {
      if (!pendingRequest.current) pendingRequest.current = { fingerprint, request_id: uuid() };
      const body = { user_id: selected.user_id, model_ids: modelIds, user_prompt: userPrompt, request_id: pendingRequest.current.request_id };
      const data = await api("/tests", { method: "POST", body: JSON.stringify(body) });
      if (!alive.current) return;
      setRecord(data);
      if (data.status !== "RUNNING") pendingRequest.current = null;
      setMode("raw");
      setPage(0);
      setHistoryRevision((value) => value + 1);
    } catch (cause) {
      if ([400, 409, 422].includes(cause.status)) pendingRequest.current = null;
      if (alive.current) setError(pendingRequest.current
        ? `${cause.message} 같은 조합으로 재시도하면 동일 요청 ID를 사용합니다.` : cause.message);
    } finally {
      running.current = false;
      if (alive.current) setBusy(false);
    }
  }

  async function load(testId) {
    const version = ++detailVersion.current;
    setLoadingRecord(true);
    setError("");
    try {
      const data = await api(`/tests/${testId}`);
      if (!alive.current || version !== detailVersion.current) return;
      setRecord(data);
      setMode("raw");
      if (data.request_id === pendingRequest.current?.request_id && data.status !== "RUNNING") {
        pendingRequest.current = null;
      }
    } catch (cause) {
      if (alive.current && version === detailVersion.current) setError(cause.message);
    } finally {
      if (alive.current && version === detailVersion.current) setLoadingRecord(false);
    }
  }

  return <div className="ccl-page">
    <header className="ccl-heading"><div><span className="ccl-eyebrow">CAPTION LAB</span><h1>인스타 캡션 테스트머신</h1><p>매장별 기본 프롬프트 편집 · 모델 2개 비교 · 실제 게시 및 문자 발송 없음</p></div><span className="ccl-badge">피드·릴스 공통 캡션</span></header>
    <p className="ccl-exchange-rate">원화 환산 기준: 1 USD = {USD_KRW_REFERENCE.rate.toLocaleString("ko-KR")}원 · {USD_KRW_REFERENCE.date} 확인 · 고정 기준환율 (자동 갱신 아님)<br />
      <a href={USD_KRW_REFERENCE.url} target="_blank" rel="noopener noreferrer">{USD_KRW_REFERENCE.source} 환율 출처</a> · 과거 히스토리도 위 기준환율로 환산 · 실제 결제 환율·카드 수수료·세금에 따라 차이 발생</p>
    {error && <div role="alert" className="ccl-error">{error}</div>}
    <section className="ccl-panel" aria-label="테스트 설정">
      <div className="ccl-search">
        <label htmlFor="caption-store">매장명 검색</label>
        <input id="caption-store" value={query} maxLength={100} disabled={busy} placeholder="띄어쓰기 없이 매장명 일부만 입력"
          onChange={(event) => { setQuery(event.target.value); setSelected(null); }} />
        {!selected && query.trim() && <div className="ccl-matches" aria-live="polite">
          {searching ? <p>검색 중...</p> : matches.length ? matches.map((store) => <button type="button" key={store.user_id}
            onClick={() => { setSelected(store); setQuery(store.store_name); setMatches([]); }}>
            <strong>{store.store_name}</strong><span>#{store.user_id} · {store.road_name}</span>
          </button>) : <p>검색 결과가 없습니다.</p>}
        </div>}
        {selected && <p className="ccl-selected">선택: {selected.store_name} · #{selected.user_id} · {selected.road_name}</p>}
      </div>
      <div className="ccl-prompt-editor">
        <div className="ccl-section-heading"><label htmlFor="caption-prompt">프롬프트</label>
          <button type="button" disabled={!promptReady || busy || userPrompt === promptPreview?.user_prompt}
            onClick={() => setUserPrompt(promptPreview.user_prompt)}>기본 프롬프트로 되돌리기</button>
        </div>
        <p id="caption-prompt-help">매장 정보가 반영된 운영 기본 문구입니다. 수정 내용은 이번 테스트의 두 모델에 동일하게 적용되며 운영 업로드에는 반영되지 않습니다.</p>
        {promptLoading && <p role="status">매장 프롬프트를 불러오는 중...</p>}
        {promptError && <div role="alert" className="ccl-error">{promptError} <button type="button" onClick={() => setPromptRevision((value) => value + 1)}>프롬프트 다시 불러오기</button></div>}
        <textarea id="caption-prompt" aria-describedby="caption-prompt-help" value={userPrompt} maxLength={30000}
          disabled={!promptReady || busy} placeholder="매장을 선택하면 기본 프롬프트가 표시됩니다."
          onChange={(event) => setUserPrompt(event.target.value)} />
        <small>{userPrompt.length.toLocaleString()} / 30,000자{promptReady && userPrompt !== promptPreview.user_prompt ? " · 수정됨" : ""}</small>
        {promptReady && !userPrompt.trim() && <p className="ccl-warning">프롬프트를 입력해 주세요.</p>}
        {(promptPreview?.warnings || []).map((warning) => <p className="ccl-warning" key={warning}>{warning}</p>)}
        {promptReady && <details><summary>시스템 프롬프트 (고정)</summary><pre>{promptPreview.system_prompt}</pre></details>}
      </div>
      <div className="ccl-models">{[0, 1].map((index) => <div key={index}>
        <label htmlFor={`caption-model-${index}`}>모델 {index === 0 ? "A" : "B"}</label>
        <select id={`caption-model-${index}`} value={modelIds[index]} disabled={busy} onChange={(event) => setModelIds((current) => current.map((id, pos) => pos === index ? event.target.value : id))}>
          {models.map((model) => <option key={model.id} value={model.id} disabled={!model.configured}>{model.label}{!model.configured ? " · API 키 미설정" : ""}</option>)}
        </select>
        {chosen[index] && <small>100만 토큰: 입력 ${chosen[index].input_price} / 출력 ${chosen[index].output_price}</small>}
      </div>)}</div>
      {modelIds[0] === modelIds[1] && <p className="ccl-warning">서로 다른 모델을 선택해 주세요.</p>}
      <div className="ccl-actions"><p>운영 히스토리와 같은 집계: 입력 + 출력(추론 포함) · 일반 단가 기준, 캐시 할인·크레딧·환율·세금 제외<br />Gemini 3.8 Flash 표시는 2026년 말까지의 단가. 실제 모델 사용 권한은 API 응답으로 확인.</p>
        <button type="button" disabled={!canRun} onClick={run}>{busy ? <><span className="ccl-spinner" />문구 생성 중</> : pendingRequest.current ? "동일 요청 재시도" : "두 모델 비교 실행"}</button>
      </div>
    </section>
    <section className="ccl-comparison" aria-label="캡션 비교 결과">
      <div className="ccl-section-heading"><div><h2>캡션 비교</h2>{record && <p>{record.store_name} · {dateLabel(record.created_at)} · {STATUS[record.status] || record.status}</p>}</div>
        <div className="ccl-tabs"><button type="button" aria-pressed={mode === "raw"} onClick={() => setMode("raw")}>AI 원본</button><button type="button" aria-pressed={mode === "final"} onClick={() => setMode("final")}>최종 게시 형태</button></div>
      </div>
      {busy && <p role="status" className="ccl-progress">동일한 프롬프트로 생성 중입니다. 각 API는 최대 90초 대기하며 자동 재호출하지 않습니다.</p>}
      {loadingRecord && <p role="status" className="ccl-progress">저장된 비교 결과를 불러오는 중입니다.</p>}
      {record?.comparison_usage && <div className="ccl-comparison-cost"><strong>두 모델 비교 1회 총 예상 비용: {record.comparison_usage.complete ? `$${record.comparison_usage.llm_estimated_cost_usd.toFixed(6)}` : "전체 계산 불가"}</strong>
        {record.comparison_usage.complete && formatEstimatedKrw(record.comparison_usage.llm_estimated_cost_usd) && <span className="ccl-krw-cost">{formatEstimatedKrw(record.comparison_usage.llm_estimated_cost_usd)}</span>}
        <span>총 토큰: {record.comparison_usage.llm_total_tokens ?? "계산 불가"}</span>{!record.comparison_usage.complete && <small>확인된 사용량의 비용: ${record.comparison_usage.known_cost_usd.toFixed(6)} ({formatEstimatedKrw(record.comparison_usage.known_cost_usd)}) · 실패·시간 초과된 호출의 실제 과금은 공급사에서 확인 필요</small>}</div>}
      {record?.status === "RUNNING" && <p className="ccl-warning">진행 중이거나 완료 기록이 없는 테스트입니다. 서버 로그 확인 전 새 요청으로 반복 실행하지 마세요.</p>}
      <div className="ccl-results"><ResultCard result={record?.results?.[0]} mode={mode} /><ResultCard result={record?.results?.[1]} mode={mode} /></div>
      {record && <details className="ccl-panel ccl-prompt"><summary>사용한 매장 정보·프롬프트 보기</summary>
        {(record.context?.warnings || []).map((warning) => <p className="ccl-warning" key={warning}>{warning}</p>)}
        <small>프롬프트 식별값: {record.prompt_hash}</small><h3>시스템 프롬프트</h3><pre>{record.system_prompt}</pre><h3>매장 정보가 반영된 프롬프트</h3><pre>{record.user_prompt}</pre>
      </details>}
    </section>
    <section className="ccl-panel" aria-label="테스트 히스토리"><div className="ccl-section-heading"><h2>테스트 히스토리</h2><span>{history.total}건 · 한국시간</span><button type="button" disabled={historyBusy || busy} onClick={() => setHistoryRevision((value) => value + 1)}>히스토리 새로고침</button></div>
      {historyBusy ? <p role="status">히스토리 조회 중...</p> : <div className="ccl-table-wrap"><table><thead><tr><th>실행 일시</th><th>매장명</th><th>비교 모델</th><th>상태</th><th>결과</th></tr></thead><tbody>
        {history.items.map((item) => <tr key={item.test_id}><td>{dateLabel(item.created_at)}</td><td>{item.store_name}<small>#{item.user_id}</small></td>
          <td>{item.model_ids.map((id) => models.find((model) => model.id === id)?.label || id).join(" / ")}</td><td>{STATUS[item.status] || item.status}</td>
          <td><button type="button" disabled={busy || loadingRecord} onClick={() => load(item.test_id)}>불러오기</button></td></tr>)}
        {!history.items.length && <tr><td colSpan={5}>저장된 테스트가 없습니다.</td></tr>}
      </tbody></table></div>}
      <div className="ccl-pagination"><button type="button" disabled={page === 0 || historyBusy} onClick={() => setPage((value) => value - 1)}>이전</button><span>{page + 1} / {Math.max(1, Math.ceil(history.total / LIMIT))}</span><button type="button" disabled={(page + 1) * LIMIT >= history.total || historyBusy} onClick={() => setPage((value) => value + 1)}>다음</button></div>
    </section>
  </div>;
}
